'use client';

import {
  COATING_COMPONENT,
  EDIT_PRESCRIPTION_COMPONENT,
  LENS_INDEX_COMPONENT,
  LENS_TYPE_COMPONENT,
} from '@/app/_lens-components/constants';
import {
  hasReachedStepInCurrentFlow,
  shouldApplyCoatingSelection,
} from '@/app/_lens-components/utils';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { NON_PRESCRIPTION_TYPES } from '@/utils/constants';
import { EPrescriptionType } from '@/utils/enums/goods';
import { useMemo } from 'react';

export interface UseShowLensInfoResult {
  showPrescription: boolean;
  showLens: boolean;
  showLensIndex: boolean;
  showCoating: boolean;
}

/** 按当前配镜步骤（nowComponent）控制 LensInfo 各区块是否展示 */
export function useShowLensInfo(isFrameProcess: boolean): UseShowLensInfoResult {
  const {
    prescriptionType,
    lensIndexPriceInfo,
    selectedLensIndexItem,
    selectedCoating,
    selectedColorItem,
    selectedAdvancedLensIndexItem,
    selectedLensType,
    selectedBrand,
    nowStep,
    allComponent,
  } = useLensProcessStore();

  return useMemo(() => {
    const hasReached = (targetComponent: string) =>
      hasReachedStepInCurrentFlow(allComponent, nowStep, targetComponent);

    const showPrescription =
      !isFrameProcess &&
      !!prescriptionType &&
      prescriptionType !== EPrescriptionType.READING_GLASSES &&
      hasReached(EDIT_PRESCRIPTION_COMPONENT);

    const hasLensSelectionToShow = !!(
      selectedAdvancedLensIndexItem ||
      selectedLensType?.merge_brand === 2 ||
      (selectedLensType?.merge_brand === 1 && selectedBrand?.brand_name) ||
      (isFrameProcess && selectedColorItem)
    );
    // frame-process（仅镜框）处方类型恒为 FRAME_ONLY，仍需在左侧展示已选镜片类型
    const showLens =
      hasReached(LENS_TYPE_COMPONENT) &&
      hasLensSelectionToShow &&
      (isFrameProcess || prescriptionType !== EPrescriptionType.FRAME_ONLY);

    // 无处方 / 仅镜框 / 太阳镜无处方：折射率价已并入镜片类型价，不单独展示
    const showSeparateLensIndex =
      !!prescriptionType && !NON_PRESCRIPTION_TYPES.includes(prescriptionType as EPrescriptionType);
    const showLensIndex =
      showSeparateLensIndex &&
      hasReached(LENS_INDEX_COMPONENT) &&
      !!selectedLensIndexItem &&
      !!lensIndexPriceInfo;

    const showCoating =
      hasReached(COATING_COMPONENT) &&
      shouldApplyCoatingSelection(prescriptionType, selectedColorItem, { isFrameProcess }) &&
      !!selectedCoating;

    return { showPrescription, showLens, showLensIndex, showCoating };
  }, [
    isFrameProcess,
    prescriptionType,
    nowStep,
    allComponent,
    selectedAdvancedLensIndexItem,
    selectedLensType,
    selectedBrand,
    selectedLensIndexItem,
    lensIndexPriceInfo,
    selectedCoating,
    selectedColorItem,
  ]);
}
